import type { TournamentSessionRequest } from '@brush/api-client';
import { Plus, Trash2 } from 'lucide-react';
import { modalControl } from '../Modal';

/**
 * The sittings of a multi-day tournament — Day 1A, Day 1B, Day 2.
 *
 * Shared by the create and edit dialogs so the two can never drift into disagreeing about what
 * a session is. The parent's name is passed in rather than read from a draft, because a new
 * row's default name comes from the tournament as it stands in the form beside it.
 *
 * Composed from `modalControl`, which carries no width: `modalInput` leads with `w-full`, and a
 * width appended after it loses to stylesheet order rather than class order — which is exactly
 * how the day box first came to swallow the entire row.
 *
 * Two lines per session rather than one. Five controls plus a delete button do not fit a dialog
 * at any width these forms use, and the name in particular needs room to be read.
 */
export function SessionEditor({
  sessions,
  parentName,
  parentDate,
  parentTime,
  onChange,
}: {
  sessions: TournamentSessionRequest[];
  parentName: string;
  parentDate: string;
  parentTime: string;
  onChange: (sessions: TournamentSessionRequest[]) => void;
}) {
  const set = (index: number, patch: Partial<TournamentSessionRequest>) =>
    onChange(sessions.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  const add = () => {
    // Next day up, so adding rows walks 1, 2, 3 without anyone typing it. Date carries from the
    // previous session and the name seeds from the tournament — both stay editable, which is
    // the whole point of storing them per session.
    const last = sessions.at(-1);
    onChange([
      ...sessions,
      {
        day: last ? last.day + 1 : 1,
        sessionName: null,
        date: last?.date ?? parentDate,
        time: last?.time ?? parentTime,
        name: parentName,
      },
    ]);
  };

  return (
    <div className="min-w-0">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="text-xs text-ink-800/60">Sessions</span>
        <span className="text-[10px] text-ink-800/40">
          Leave the session letter blank when a day runs once.
        </span>
      </div>

      <div className="space-y-2">
        {sessions.map((session, index) => (
          // Index as key: rows are positional and have no server identity until saved.
          <div
            key={index}
            className="min-w-0 space-y-1.5 rounded-lg border border-ink-800/10 bg-ink-800/[0.02] p-3"
          >
            {/* Wraps rather than shrinks. Native date and time inputs have a substantial
                min-content width — a locale rendering "06:00 PM" plus a picker icon needs every
                pixel of w-36 — so squeezing them into a single row on a narrow screen clipped
                the time and collapsed the date until its label sat on top of the next one.
                Fixed widths plus flex-wrap let a row become two lines instead. */}
            <div className="flex flex-wrap items-end gap-2">
              <label className="block w-16 shrink-0">
                <span className="mb-1 block text-[10px] uppercase tracking-wide text-ink-800/45">
                  Day
                </span>
                <input
                  type="number"
                  min={1}
                  value={session.day}
                  onChange={(e) => set(index, { day: Number(e.target.value) })}
                  className={`w-full ${modalControl}`}
                />
              </label>

              <label className="block w-16 shrink-0">
                <span className="mb-1 block text-[10px] uppercase tracking-wide text-ink-800/45">
                  Session
                </span>
                <input
                  value={session.sessionName ?? ''}
                  onChange={(e) => set(index, { sessionName: e.target.value || null })}
                  placeholder="A"
                  maxLength={16}
                  className={`w-full ${modalControl}`}
                />
              </label>

              <label className="block w-40 shrink-0">
                <span className="mb-1 block text-[10px] uppercase tracking-wide text-ink-800/45">
                  Date
                </span>
                <input
                  type="date"
                  value={session.date}
                  onChange={(e) => set(index, { date: e.target.value })}
                  className={`w-full ${modalControl}`}
                />
              </label>

              <label className="block w-36 shrink-0">
                <span className="mb-1 block text-[10px] uppercase tracking-wide text-ink-800/45">
                  Time
                </span>
                <input
                  type="time"
                  value={session.time}
                  onChange={(e) => set(index, { time: e.target.value })}
                  className={`w-full ${modalControl}`}
                />
              </label>

              {/* ml-auto so it hugs the right edge on a wide row and still sits sensibly once
                  the row has wrapped. */}
              <button
                type="button"
                onClick={() => onChange(sessions.filter((_, i) => i !== index))}
                aria-label={`Remove session ${index + 1}`}
                className="ml-auto shrink-0 p-2 text-ink-800/40 hover:text-chip-500"
              >
                <Trash2 className="size-4" />
              </button>
            </div>

            <input
              value={session.name ?? ''}
              onChange={(e) => set(index, { name: e.target.value })}
              placeholder={parentName || 'Session name'}
              aria-label={`Session ${index + 1} name`}
              className={`w-full ${modalControl} text-xs`}
            />
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={add}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-felt-700 hover:text-felt-900"
      >
        <Plus className="size-3.5" /> Add session
      </button>

      {sessions.length === 0 ? (
        <p className="mt-2 text-xs text-ink-800/50">
          No sessions yet. A multi-day tournament can be saved without them — the schedule can
          follow later.
        </p>
      ) : null}
    </div>
  );
}
